import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import ModalWrapper from './ModalWrapper';

const ENTRY_FIELDS = {
  kith: [
    { n: 'name', l: 'Kith Name', t: 'i', p: 'e.g. The Wandering Badger' },
    { n: 'natures', l: 'Natures (one per line)', t: 'a', p: 'Gentle\nCurious\nFierce' },
    { n: 'notes', l: 'Description', t: 'a', p: 'What makes this kith special?' },
  ],
  place: [
    { n: 'name', l: 'Place Name', t: 'i', p: 'e.g. The Hollow Mill' },
    { n: 'region', l: 'Region / Season', t: 'i', p: 'e.g. Autumn foothills' },
    { n: 'notes', l: 'Description & Lore', t: 'a', p: 'Describe this place...' },
  ],
  session: [
    { n: 'name', l: 'Session Title', t: 'i', p: 'e.g. Session 3: The Millpond' },
    { n: 'players', l: 'Players Present', t: 'i', p: 'Comma-separated names' },
    { n: 'notes', l: 'What Happened', t: 'a', p: 'Moments, discoveries, choices...' },
  ],
  rule: [
    { n: 'name', l: 'Rule / Move Name', t: 'i', p: 'e.g. The Shared Meal' },
    { n: 'trigger', l: 'When to Use', t: 'i', p: 'When the group gathers to eat...' },
    { n: 'notes', l: 'Effect & Notes', t: 'a', p: 'What happens? Any roll? Any cost?' },
  ],
};

export default function EntryModal({ onClose, initialType, existingEntry, onSaved }) {
  const [type, setType] = useState(existingEntry?.type || initialType || 'kith');
  const [form, setForm] = useState({});

  useEffect(() => {
    if (existingEntry) {
      setType(existingEntry.type);
      const fields = ENTRY_FIELDS[existingEntry.type] || [];
      const prefilled = {};
      fields.forEach(f => {
        prefilled[f.n] = existingEntry[f.n] || '';
      });
      setForm(prefilled);
    }
  }, [existingEntry]);

  const handleTypeChange = (newType) => {
    setType(newType);
    setForm({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) return alert('Please enter a name.');

    const entry = { type, ...form };
    if (existingEntry) {
      await supabase.from('entries').update(entry).eq('id', existingEntry.id);
    } else {
      await supabase.from('entries').insert([entry]);
    }
    
    onSaved?.();
    onClose();
  };

  const fields = ENTRY_FIELDS[type] || [];

  return (
    <ModalWrapper title={existingEntry ? 'Edit Entry' : 'New Repository Entry'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block font-lora text-sm font-medium text-ink-mid mb-1">Entry Type</label>
          <select 
            className="form-input-styled cursor-pointer"
            value={type} 
            onChange={e => handleTypeChange(e.target.value)}
            disabled={!!existingEntry}
          >
            <option value="kith">🌿 Custom Kith</option>
            <option value="place">🗺️ Place / Location</option>
            <option value="session">🕯️ Journey Note / Session Log</option>
            <option value="rule">📜 House Rule / Custom Move</option>
          </select>
        </div>

        {fields.map(f => (
          <div key={f.n}>
            <label className="block font-lora text-sm font-medium text-ink-mid mb-1">{f.l}</label>
            {f.t === 'a' ? (
              <textarea 
                className="form-input-styled min-h-[90px]"
                placeholder={f.p}
                value={form[f.n] || ''}
                onChange={e => setForm({...form, [f.n]: e.target.value})}
              />
            ) : (
              <input 
                className="form-input-styled"
                placeholder={f.p}
                value={form[f.n] || ''}
                onChange={e => setForm({...form, [f.n]: e.target.value})}
              />
            )}
          </div>
        ))}

        <div className="flex gap-3 justify-end mt-6 pt-2">
          <button type="button" onClick={onClose} className="bg-transparent border-[1.5px] border-parchment-dark rounded px-4 py-2 font-lora text-[0.84rem] text-ink-mid cursor-pointer hover:bg-parchment-dark/30">
            Cancel
          </button>
          <button type="submit" className="bg-ink text-parchment border-none rounded px-5 py-2 font-lora text-[0.84rem] cursor-pointer hover:bg-ink-mid">
            {existingEntry ? 'Save Changes' : 'Add to Repository'}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
}